import { createRoutine } from 'redux-saga-routines';
import { replace } from 'connected-react-router';
import { takeLatest, put, select } from 'redux-saga/effects';
import moment from 'moment';
import { get } from 'lodash/fp';
import { shouldPolyfill as shouldPolyfillNumberFormat } from '@formatjs/intl-numberformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillDatetime } from '@formatjs/intl-datetimeformat/should-polyfill';
import { getUserContinent, getUserCountry, locales } from '@whitelabel/helpers/site';
import { retry } from '@whitelabel/helpers/utils';
import { localizeLocation } from '@whitelabel/helpers/i18n';
import { CURRENT_LOCALE } from '@whitelabel/helpers/constants';

export const updateLocale = createRoutine('locales/UPDATE_LOCALE');

export const loadLocale = async (locale: string) => {
  const { momentCode } = locales[locale];
  const code = momentCode.includes('zh') ? 'zh' : momentCode;
  const imports = [];

  const unsupportedNumberFormatLocale = shouldPolyfillNumberFormat(code);
  if (unsupportedNumberFormatLocale) {
    imports.push(
      import(
        `@formatjs/intl-numberformat/locale-data/${unsupportedNumberFormatLocale}` /* webpackChunkName: `intl-numberformat-locale-data` */
      ),
    );
  }

  const unsupportedDatetimeLocale = shouldPolyfillDatetime(code);
  if (unsupportedDatetimeLocale) {
    imports.push(import('@formatjs/intl-datetimeformat/add-all-tz'));
    imports.push(
      import(
        `@formatjs/intl-datetimeformat/locale-data/${unsupportedDatetimeLocale}` /* webpackChunkName: `intl-datetimeformat-locale-data` */
      ),
    );
  }

  if (!Intl.PluralRules) {
    switch (code) {
      case 'en':
      case 'de':
      case 'es':
      case 'ja':
      case 'id':
      case 'ko':
      case 'ms':
      case 'ru':
      case 'th':
      case 'tr':
      case 'uk':
      case 'vi':
      case 'zh':
      case 'da':
      case 'fr':
      case 'it':
      case 'nl':
      case 'pl':
      case 'pt':
      case 'sv':
      case 'fi':
      case 'cs':
      case 'bg':
      case 'ca':
      case 'fil':
      case 'hr':
      case 'et':
      case 'el':
      case 'hu':
      case 'lv':
      case 'lt':
      case 'ro':
      case 'sr':
      case 'sk':
      case 'sl':
      case 'sw':
      case 'is':
      case 'ar':
      case 'he':
      case 'mt':
      case 'az':
      case 'ka':
        imports.push(import(`@formatjs/intl-pluralrules/locale-data/${code}`));
        break;
      case 'pt-br':
        // Portugese as spoken in Portugal (pt-PT; pt is Brazilian Portuguese) is available as pt_PT
        imports.push(import(`@formatjs/intl-pluralrules/locale-data/pt`));
        break;
      case 'no':
        // Norwegian bokmål (nb) is the dominant language in Norway
        imports.push(import(`@formatjs/intl-pluralrules/locale-data/nb`));
        break;
      case 'es-mx':
        imports.push(import(`@formatjs/intl-pluralrules/locale-data/es`));
        break;
      default:
        break;
    }
  }

  if (!Intl.RelativeTimeFormat) {
    switch (code) {
      case 'en':
      case 'de':
      case 'es':
      case 'ja':
      case 'id':
      case 'ko':
      case 'ms':
      case 'ru':
      case 'th':
      case 'tr':
      case 'uk':
      case 'vi':
      case 'zh':
      case 'da':
      case 'fr':
      case 'it':
      case 'nl':
      case 'pl':
      case 'pt':
      case 'sv':
      case 'fi':
      case 'cs':
      case 'bg':
      case 'ca':
      case 'fil':
      case 'hr':
      case 'et':
      case 'el':
      case 'hu':
      case 'lv':
      case 'lt':
      case 'ro':
      case 'sr':
      case 'sk':
      case 'sl':
      case 'sw':
      case 'is':
      case 'ar':
      case 'he':
      case 'mt':
      case 'az':
      case 'ka':
        imports.push(import(`@formatjs/intl-relativetimeformat/locale-data/${code}`));
        break;
      case 'pt-br':
        // Portugese as spoken in Portugal (pt-PT; pt is Brazilian Portuguese) is available as pt_PT
        imports.push(import(`@formatjs/intl-relativetimeformat/locale-data/pt`));
        break;
      case 'no':
        // Norwegian bokmål (nb) is the dominant language in Norway
        imports.push(import(`@formatjs/intl-relativetimeformat/locale-data/nb`));
        break;
      case 'es-mx':
        imports.push(import(`@formatjs/intl-relativetimeformat/locale-data/es`));
        break;
      default:
    }
  }

  // @ts-ignore
  if (!Intl.DisplayNames) {
    switch (code) {
      case 'en':
      case 'de':
      case 'es':
      case 'ja':
      case 'id':
      case 'ko':
      case 'ms':
      case 'ru':
      case 'th':
      case 'tr':
      case 'uk':
      case 'vi':
      case 'zh':
      case 'da':
      case 'fr':
      case 'it':
      case 'nl':
      case 'pl':
      case 'pt':
      case 'sv':
      case 'fi':
      case 'cs':
      case 'bg':
      case 'ca':
      case 'fil':
      case 'hr':
      case 'et':
      case 'el':
      case 'hu':
      case 'lv':
      case 'lt':
      case 'ro':
      case 'sr':
      case 'sk':
      case 'sl':
      case 'sw':
      case 'is':
      case 'ar':
      case 'he':
      case 'mt':
      case 'az':
      case 'ka':
        imports.push(import(`@formatjs/intl-displaynames/locale-data/${code}`));
        break;
      case 'pt-br':
        // Portugese as spoken in Portugal (pt-PT; pt is Brazilian Portuguese) is available as pt_PT
        // @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '@for... Remove this comment to see the full error message
        imports.push(import(`@formatjs/intl-displaynames/locale-data/pt`));
        break;
      case 'no':
        // Norwegian bokmål (nb) is the dominant language in Norway
        // @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '@for... Remove this comment to see the full error message
        imports.push(import(`@formatjs/intl-displaynames/locale-data/nb`));
        break;
      case 'es-mx':
        // @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '@for... Remove this comment to see the full error message
        imports.push(import(`@formatjs/intl-displaynames/locale-data/es`));
        break;
      default:
    }
  }

  switch (code) {
    case 'de':
    case 'ja':
    case 'es':
    case 'es-mx':
    case 'id':
    case 'ko':
    case 'ms':
    case 'ru':
    case 'th':
    case 'tr':
    case 'uk':
    case 'vi':
    case 'da':
    case 'fr':
    case 'it':
    case 'nl':
    case 'pl':
    case 'pt':
    case 'sv':
    case 'pt-br':
    case 'fi':
    case 'cs':
    case 'bg':
    case 'ca':
    case 'hr':
    case 'et':
    case 'el':
    case 'hu':
    case 'lv':
    case 'lt':
    case 'ro':
    case 'sr':
    case 'sk':
    case 'sl':
    case 'sw':
    case 'is':
    case 'fil':
    case 'ar':
    case 'he':
    case 'mt':
    case 'az':
    case 'ka':
      imports.push(retry(() => import(`moment/locale/${code}` /* webpackChunkName: `moment-locale` */)));
      break;
    case 'zh-hant':
      // @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'moment/locale/zh-hk' or its co... Remove this comment to see the full error message
      imports.push(retry(() => import(`moment/locale/zh-hk` /* webpackChunkName: `moment-locale-zh-hk` */)));
      break;
    case 'zh-hans':
      // @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'moment/locale/zh-cn' or its co... Remove this comment to see the full error message
      imports.push(retry(() => import(`moment/locale/zh-cn` /* webpackChunkName: `moment-locale-zh-cn` */)));
      break;
    case 'no':
      // Norwegian bokmål (nb) is the dominant language in Norway
      // @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'moment/locale/nb' or its corre... Remove this comment to see the full error message
      imports.push(retry(() => import(`moment/locale/nb` /* webpackChunkName: `moment-locale-no` */)));
      break;
    default:
      // @ts-expect-error for moment.js we use en-au as default
      imports.push(retry(() => import(`moment/locale/en-au` /* webpackChunkName: `moment-locale-en-au` */)));
  }

  if (imports.length > 0) await Promise.all(imports);
  moment.locale(momentCode);
};
// @ts-expect-error ts-migrate(7031) FIXME: Binding element 'locale' implicitly has an 'any' t... Remove this comment to see the full error message
function* handleUpdateLocale({ payload: locale }: ReturnType<{ type: typeof updateLocale.TRIGGER; payload: string }>) {
  try {
    const prevLocale: string = yield select(get('intl.locale'));
    const location: Location = yield select(get('router.location'));

    if (locale !== prevLocale) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const newLocale =
        locale === 'en' && (getUserCountry() === 'US' || getUserContinent() === 'SA') ? 'en-us' : locale;
      sessionStorage.setItem(CURRENT_LOCALE, newLocale);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { pathname, search, hash, state } = localizeLocation(location, newLocale);
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      yield put(replace(`${pathname}${search}${hash}`, state));
      window.location.reload();
    }
  } catch (error) {
    yield put(updateLocale.failure(error));
  }
}
export function* localesSaga() {
  yield takeLatest(updateLocale.TRIGGER, handleUpdateLocale);
}
